import React, { useRef, useCallback, useState, useEffect, useMemo } from 'react';
import Webcam from 'react-webcam';
import DetectionOverlay from './DetectionOverlay';
import ColorLegend from './ColorLegend';
import './WebcamCapture.css';

const API_URL = 'https://cv.ainnect.me';

const WebcamCapture = ({ selectedModel }) => {
  const webcamRef = useRef(null);
  const [detections, setDetections] = useState(null);
  const [isDetecting, setIsDetecting] = useState(false);
  const [error, setError] = useState(null);
  const [facingMode, setFacingMode] = useState('user'); // 'user' | 'environment'
  const [stats, setStats] = useState({
    inferenceTime: 0,
    numDetections: 0,
    lastUpdate: null
  });

  const videoConstraints = useMemo(
    () => ({
      width: 1280,
      height: 720,
      facingMode,
      frameRate: { ideal: 30, max: 60 },
    }),
    [facingMode]
  );

  // Đảm bảo xin quyền camera một lần (cần cho mobile)
  useEffect(() => {
    const askPermission = async () => {
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) return;
        await navigator.mediaDevices.getUserMedia({ video: true });
      } catch (err) {
        console.error('Camera permission error:', err);
      }
    };
    askPermission();
  }, []);

  const captureAndSend = useCallback(async () => {
    if (!webcamRef.current || isDetecting) return;

    const imageSrc = webcamRef.current.getScreenshot({
      width: 640,  // Reduced resolution for faster processing
      height: 360,
      quality: 0.8 // Slightly lower quality for speed
    });
    if (!imageSrc) return;

    setIsDetecting(true);
    setError(null);

    try {
      const response = await fetch(imageSrc);
      const blob = await response.blob();

      // Create form data
      const formData = new FormData();
      formData.append('image', blob, 'capture.jpg');

      // Send to AI server with timeout for real-time performance
      const detectResponse = await Promise.race([
        fetch(`${API_URL}/detect?model=${selectedModel || 'best_v8s'}`, {
          method: 'POST',
          body: formData,
        }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Request timeout')), 10000)
        )
      ]);

      if (!detectResponse.ok) {
        throw new Error(`Server error: ${detectResponse.status}`);
      }

      const result = await detectResponse.json();
      setDetections(result);
      setStats({
        inferenceTime: result.inference_ms,
        numDetections: result.num_detections,
        lastUpdate: new Date().toLocaleTimeString()
      });
    } catch (err) {
      console.error('Detection error:', err);
      setError(err.message);
    } finally {
      setIsDetecting(false);
    }
  }, [isDetecting, selectedModel]);

  useEffect(() => {
    const interval = setInterval(() => {
      captureAndSend();
    }, 200);

    return () => clearInterval(interval);
  }, [captureAndSend]);

  return (
    <div className="webcam-container">
      <div className="video-wrapper">
        <Webcam
          audio={false}
          ref={webcamRef}
          screenshotFormat="image/jpeg"
          videoConstraints={videoConstraints}
          className="webcam-video"
        />
        {detections && (
          <DetectionOverlay
            detections={detections.boxes}
            // Sử dụng đúng kích thước frame gửi lên server (screenshot 640x360)
            imageWidth={640}
            imageHeight={360}
            videoWidth={videoConstraints.width}
            videoHeight={videoConstraints.height}
          />
        )}
      </div>

      <div className="controls">
        <div className="camera-selector">
          <label htmlFor="camera-select">Camera:</label>
          <select
            id="camera-select"
            value={facingMode}
            onChange={(e) => setFacingMode(e.target.value)}
          >
            <option value="user">Camera trước</option>
            <option value="environment">Camera sau</option>
          </select>
        </div>
        <button onClick={captureAndSend} className="capture-btn">
          Phát hiện thủ công
        </button>
      </div>

      <div className="stats-panel">
        <div className="stat-item">
          <span className="stat-label">Trạng thái</span>
          <span className={`stat-value ${isDetecting ? 'detecting' : 'ready'}`}>
            {isDetecting ? '🔍 Đang xử lý' : '✅ Sẵn sàng'}
          </span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Phát hiện</span>
          <span className="stat-value">{stats.numDetections}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Thời gian xử lý</span>
          <span className="stat-value">{Math.round(stats.inferenceTime)}ms</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Cập nhật cuối</span>
          <span className="stat-value">{stats.lastUpdate || 'Chưa có'}</span>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}

      <ColorLegend detections={detections?.boxes} />
    </div>
  );
};

export default WebcamCapture;
